/*
  Complete Industry Dataset (categorized)
  ------------------------------------------------------------
  Purpose
  - Provides the canonical list of industries grouped by category for the ROI calculator and UI selectors.
  - Each industry includes an id, display name, and waste range used for savings estimates.

  Data Model
  - Industry fields (per line inside `industries` arrays):
      • id (string): Stable identifier, kebab-case. MUST match ids used in `src/data/industryTasks.js`.
      • name (string): Human-readable industry name for UI.
      • wasteLow (number, 0..1): Lower bound of industry process waste. Derived from external benchmarks and internal analyses.
      • wasteHigh (number, 0..1): Upper bound of industry process waste. Derived similarly; keep conservative.
    Category field:
      • baseAutomationPotential (number, 0..1): Category-level baseline used for UI/auto-config heuristics.

  Derivation Guidance
  - Waste bounds should be sourced from: analyst reports, industry benchmarks, published studies, internal client results.
  - Keep notes in commit messages when adjusting ranges; prefer incremental, evidence-based changes.
  - Ensure alignment with `industryTasks.js` agents coverage and ROI logic assumptions.

  Maintenance
  - Review quarterly; update when new credible data is available.
  - When adding a new industry:
      1) Add it here in the correct category order.
      2) Add a matching `agents` array in `src/data/industryTasks.js` with metrics.
      3) Keep the same `id` across files.
*/

/**
 * @typedef {Object} Industry
 * @property {string} id - Stable, kebab-case identifier.
 * @property {string} name - Display name.
 * @property {number} wasteLow - Lower bound of industry waste (0..1).
 * @property {number} wasteHigh - Upper bound of industry waste (0..1).
 */

/**
 * @typedef {Object} IndustryCategory
 * @property {number} baseAutomationPotential - Category baseline for automation (0..1).
 * @property {Industry[]} industries - List of industries in this category.
 */
import { industryTasks } from './industryTasks';

/** @type {{ [category: string]: IndustryCategory }} */
const industryCategories = {
  // Manufacturing and Industrial (simplified)
  manufacturing: {
    baseAutomationPotential: 0.70,
    industries: [
      { id: 'manufacturing', name: 'Manufacturing', wasteLow: 0.15, wasteHigh: 0.30 },
      { id: 'food-beverage', name: 'Food and Beverage', wasteLow: 0.20, wasteHigh: 0.30 },
      { id: 'chemicals', name: 'Chemicals', wasteLow: 0.20, wasteHigh: 0.30 },
      { id: 'pharmaceuticals', name: 'Pharmaceuticals', wasteLow: 0.20, wasteHigh: 0.40 },
      { id: 'automotive', name: 'Automotive', wasteLow: 0.20, wasteHigh: 0.30 },
      { id: 'aerospace-defense', name: 'Aerospace and Defense', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'electronics', name: 'Electronics', wasteLow: 0.20, wasteHigh: 0.30 },
      { id: 'textiles-apparel', name: 'Textiles and Apparel', wasteLow: 0.25, wasteHigh: 0.35 },
      { id: 'furniture', name: 'Furniture', wasteLow: 0.25, wasteHigh: 0.35 },
      { id: 'paper', name: 'Paper', wasteLow: 0.20, wasteHigh: 0.30 },
      { id: 'plastics', name: 'Plastics', wasteLow: 0.20, wasteHigh: 0.30 }
    ]
  },
  
  // Services (simplified)
  services: {
    baseAutomationPotential: 0.65,
    industries: [
      { id: 'professional-services', name: 'Professional Services', wasteLow: 0.30, wasteHigh: 0.50 },
      { id: 'legal-services', name: 'Legal Services', wasteLow: 0.30, wasteHigh: 0.50 },
      { id: 'financial-services', name: 'Financial Services', wasteLow: 0.15, wasteHigh: 0.30 },
      { id: 'insurance', name: 'Insurance', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'real-estate', name: 'Real Estate', wasteLow: 0.25, wasteHigh: 0.35 },
      { id: 'advertising', name: 'Advertising', wasteLow: 0.30, wasteHigh: 0.50 },
      { id: 'healthcare', name: 'Healthcare', wasteLow: 0.25, wasteHigh: 0.40 },
      { id: 'education', name: 'Education', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'hospitality', name: 'Hospitality', wasteLow: 0.30, wasteHigh: 0.50 },
      { id: 'retail', name: 'Retail', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'construction', name: 'Construction', wasteLow: 0.25, wasteHigh: 0.40 }
    ]
  },
  
  // Technology (simplified)
  technology: {
    baseAutomationPotential: 0.75,
    industries: [
      { id: 'software', name: 'Software', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'ecommerce', name: 'E-commerce', wasteLow: 0.15, wasteHigh: 0.30 },
      { id: 'telecom', name: 'Telecommunications', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'it-services', name: 'IT Services', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'data-centers', name: 'Data Centers', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'ai-ml', name: 'AI/ML Services', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'cloud-services', name: 'Cloud Services', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'cybersecurity', name: 'Cybersecurity', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'gaming', name: 'Gaming', wasteLow: 0.25, wasteHigh: 0.40 },
      { id: 'vr-ar', name: 'VR/AR', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'blockchain', name: 'Blockchain', wasteLow: 0.20, wasteHigh: 0.35 }
    ]
  },
  
  // Energy and Utilities (simplified)
  energy: {
    baseAutomationPotential: 0.60,
    industries: [
      { id: 'oil-gas', name: 'Oil & Gas', wasteLow: 0.15, wasteHigh: 0.30 },
      { id: 'renewable-energy', name: 'Renewable Energy', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'utilities', name: 'Utilities', wasteLow: 0.15, wasteHigh: 0.30 },
      { id: 'mining', name: 'Mining', wasteLow: 0.25, wasteHigh: 0.35 },
      { id: 'waste-management', name: 'Waste Management', wasteLow: 0.25, wasteHigh: 0.40 },
      { id: 'water-treatment', name: 'Water Treatment', wasteLow: 0.20, wasteHigh: 0.35 }
    ]
  },
  
  // Transportation and Logistics (simplified)
  transportation: {
    baseAutomationPotential: 0.65,
    industries: [
      { id: 'logistics', name: 'Logistics', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'shipping', name: 'Shipping', wasteLow: 0.25, wasteHigh: 0.40 },
      { id: 'trucking', name: 'Trucking', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'aviation', name: 'Aviation', wasteLow: 0.20, wasteHigh: 0.35 },
      { id: 'rail', name: 'Rail', wasteLow: 0.25, wasteHigh: 0.35 },
      { id: 'public-transport', name: 'Public Transport', wasteLow: 0.25, wasteHigh: 0.40 }
    ]
  }
};

// Flatten the industry data and add automation potential
export const industryData = [];

Object.entries(industryCategories).forEach(([category, data]) => {
  data.industries.forEach(industry => {
    // Add some variation to automation potential based on industry specifics
    const automationVariation = (Math.random() * 0.2) - 0.1; // ±10% variation
    const automationPotential = Math.min(0.9, Math.max(0.3, 
      data.baseAutomationPotential + automationVariation));
      
    // Merge tasks, job samples, agents, and optional taskScores if available
    const extras = industryTasks[industry.id] || {};
    const tasks = Array.isArray(extras.tasks) ? extras.tasks : [];
    const jobSamples = Array.isArray(extras.jobSamples) ? extras.jobSamples : [];
    const agents = Array.isArray(extras.agents) ? extras.agents : undefined;
    const taskScores = Array.isArray(extras.taskScores) ? extras.taskScores : undefined;

    industryData.push({
      ...industry,
      category,
      automationPotential: parseFloat(automationPotential.toFixed(2)),
      description: `${industry.name} services and operations`,
      tasks,
      jobSamples,
      ...(agents ? { agents } : {}),
      ...(taskScores ? { taskScores } : {})
    });
  });
});

// Admin waste rates
export const adminWasteRates = {
  wasteLow: 0.20,  // 20%
  wasteHigh: 0.30  // 30%
};

// Function to calculate potential savings
export const calculateSavings = (industryId, employeeCount, annualRevenue, automationMaturity) => {
  const industry = industryData.find(i => i.id === industryId);
  if (!industry) return null;

  // Calculate automation factor based on maturity (1-5 scale)
  const automationFactor = 0.1 + (automationMaturity * 0.05);
  
  // Calculate combined waste rate (industry waste * admin waste)
  const combinedWasteRate = {
    low: industry.wasteLow * adminWasteRates.wasteLow,
    high: industry.wasteHigh * adminWasteRates.wasteHigh
  };

  // Calculate potential savings
  const potentialSavings = {
    low: annualRevenue * combinedWasteRate.low * industry.automationPotential * automationFactor,
    high: annualRevenue * combinedWasteRate.high * industry.automationPotential * automationFactor
  };

  // Calculate equivalent FTE savings (assuming average employee cost of $50k)
  const avgEmployeeCost = 50000;
  const fteSavings = {
    low: potentialSavings.low / avgEmployeeCost,
    high: potentialSavings.high / avgEmployeeCost
  };

  return {
    potentialSavings,
    fteSavings,
    combinedWasteRate,
    automationPotential: industry.automationPotential
  };
};
